import React, { useState, useEffect, useRef } from 'react';
import SelectableBall from './SelectableBall';
import './Live.css';

const MicMonitor = () => {
    const [audioContext, setAudioContext] = useState(null);
    const [micStream, setMicStream] = useState(null);
    const [audioSource, setAudioSource] = useState(null);
    const gainNode = useRef(null);
    const [convolverNode, setConvolverNode] = useState(null);
    const [impulseResponse1, setImpulseResponse1] = useState(null);
    const [impulseResponse2, setImpulseResponse2] = useState(null);
    const [impulseResponse3, setImpulseResponse3] = useState(null);
    const [impulseResponse4, setImpulseResponse4] = useState(null);
    const [impulseResponse5, setImpulseResponse5] = useState(null);
    const [impulseResponse6, setImpulseResponse6] = useState(null);
    const [impulseResponse7, setImpulseResponse7] = useState(null);
    const [impulseResponse8, setImpulseResponse8] = useState(null);
    const [impulseResponse9, setImpulseResponse9] = useState(null);
    const [status, setStatus] = useState("マイクはオフです。");
    const [isStreaming, setIsStreaming] = useState(false);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    useEffect(() => {
        const loadImpulseResponses = async () => {
            try {
                const response1 = await fetch('data_30_73.wav');
                const arrayBuffer1 = await response1.arrayBuffer();
                const audioBuffer1 = await audioContext.decodeAudioData(arrayBuffer1);
                setImpulseResponse1(audioBuffer1);

                const response2 = await fetch('data_30_48.wav');
                const arrayBuffer2 = await response2.arrayBuffer();
                const audioBuffer2 = await audioContext.decodeAudioData(arrayBuffer2);
                setImpulseResponse2(audioBuffer2);

                const response3 = await fetch('data_30_37.wav');
                const arrayBuffer3 = await response3.arrayBuffer();
                const audioBuffer3 = await audioContext.decodeAudioData(arrayBuffer3);
                setImpulseResponse3(audioBuffer3);

                const response4 = await fetch('data_30_23.wav');
                const arrayBuffer4 = await response4.arrayBuffer();
                const audioBuffer4 = await audioContext.decodeAudioData(arrayBuffer4);
                setImpulseResponse4(audioBuffer4);

                const response5 = await fetch('data_30_19.wav');
                const arrayBuffer5 = await response5.arrayBuffer();
                const audioBuffer5 = await audioContext.decodeAudioData(arrayBuffer5);
                setImpulseResponse5(audioBuffer5);

                const response6 = await fetch('data_30_15.wav');
                const arrayBuffer6 = await response6.arrayBuffer();
                const audioBuffer6 = await audioContext.decodeAudioData(arrayBuffer6);
                setImpulseResponse6(audioBuffer6);

                const response7 = await fetch('data_30_1.wav');
                const arrayBuffer7 = await response7.arrayBuffer();
                const audioBuffer7 = await audioContext.decodeAudioData(arrayBuffer7);
                setImpulseResponse7(audioBuffer7);

                const response8 = await fetch('data_30_62.wav');
                const arrayBuffer8 = await response8.arrayBuffer();
                const audioBuffer8 = await audioContext.decodeAudioData(arrayBuffer8);
                setImpulseResponse8(audioBuffer8);

                const response9 = await fetch('data_30_74.wav');
                const arrayBuffer9 = await response9.arrayBuffer();
                const audioBuffer9 = await audioContext.decodeAudioData(arrayBuffer9);
                setImpulseResponse9(audioBuffer9);
            } catch (err) {
                console.error('インパルス応答の読み込みに失敗しました:', err);
            }
        };

        if (audioContext) {
            loadImpulseResponses();
        }
    }, [audioContext]);

    useEffect(() => {
        const getAudioDevices = async () => {
            try {
                const deviceInfos = await navigator.mediaDevices.enumerateDevices();
                const audioDevices = deviceInfos.filter(device => device.kind === 'audioinput');
                setDevices(audioDevices);
            } catch (err) {
                console.error('オーディオデバイスの取得に失敗しました:', err);
            }
        };

        getAudioDevices();
    }, []);

    const handleDeviceChange = (event) => {
        const deviceId = event.target.value;
        setSelectedDeviceId(deviceId);

        if (!audioContext) {
            const newAudioContext = new (window.AudioContext || window.webkitAudioContext)();
            setAudioContext(newAudioContext);
        }
    };

    // ゲイン調整のスライドバーの変更時に呼び出される関数
    const handleGainChange = (event) => {
        if (gainNode.current) {
            const newValue = parseFloat(event.target.value);
            gainNode.current.gain.value = newValue;  // useRefで参照しているgainNodeを更新
            console.log('Gain value set to:', newValue);
        }
    };

    const startMonitoring = async () => {
        if (!audioContext) {
            console.error('AudioContextが作成されていません。');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: selectedDeviceId,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                }
            });
            setMicStream(stream);

            const source = audioContext.createMediaStreamSource(stream);
            setAudioSource(source);

            gainNode.current = audioContext.createGain();
            gainNode.current.gain.value = 1;

            const convolver = audioContext.createConvolver();
            convolver.normalize = false;
            setConvolverNode(convolver);

            // 初期インパルス応答を設定
            convolver.buffer = impulseResponse5;

            // 音声ソースをConvolverNodeに接続し、ConvolverNodeをゲインノードに接続
            source.connect(convolver);
            convolver.connect(gainNode.current);
            gainNode.current.connect(audioContext.destination);

            setStatus("マイクはオンです。");
            setIsStreaming(true);
        } catch (err) {
            console.error('マイクの取得に失敗しました:', err);
            setStatus("マイクの取得に失敗しました。");
        } finally {
            setIsButtonDisabled(false);
        }
    };

    const stopMonitoring = () => {
        if (micStream) {
            micStream.getTracks().forEach(track => track.stop());
            setMicStream(null);
            setAudioSource(null);
            gainNode.current = null;
            setConvolverNode(null);
            setIsStreaming(false);
            setStatus("マイクはオフです。");
            setIsButtonDisabled(false);
        }
    };

    const toggleMonitoring = () => {
        setIsButtonDisabled(true);
        setIsPassThru(false);
        if (isStreaming) {
            stopMonitoring();
        } else {
            startMonitoring();
        }
    };

    const [isPassThru, setIsPassThru] = useState(false);

    const togglePassThru = () => {
        if (isStreaming) {
            setIsPassThru(prevState => !prevState);
            if (isPassThru) {
                // パススルーをオフにする
                audioSource.disconnect(gainNode.current);
                audioSource.connect(convolverNode);
                convolverNode.connect(gainNode.current);
            } else {
                // パススルーをオンにする
                audioSource.disconnect(convolverNode);
                audioSource.connect(gainNode.current);
            }
        }
    };

    const switchToImpulseResponse1 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse1;
        }
    };

    const switchToImpulseResponse2 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse2;
        }
    };

    const switchToImpulseResponse3 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse3;
        }
    };

    const switchToImpulseResponse4 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse4;
        }
    };

    const switchToImpulseResponse5 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse5;
        }
    };

    const switchToImpulseResponse6 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse6;
        }
    };

    const switchToImpulseResponse7 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse7;
        }
    };

    const switchToImpulseResponse8 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse8;
        }
    };

    const switchToImpulseResponse9 = () => {
        if (convolverNode) {
            convolverNode.buffer = impulseResponse9;
        }
    };

    return (
        <>
            <div className="live-container">
                <div className="live-column-container">
                    <div className="live-select-container">
                        <p className="label-live-mic">🎙️ マイク設定</p>
                        <select className="mic-select" onChange={handleDeviceChange} disabled={isStreaming} value={selectedDeviceId}>
                            <option value="">▼音声入力デバイスを選択してください</option>
                            {devices.map(device => (
                                <option key={device.deviceId} value={device.deviceId}>
                                    {device.label || `Device ${device.deviceId}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="live-control-item">
                        <p className="label-live-gain">🔊 ゲイン調整</p>
                        <input
                            type="range"
                            min="0"
                            max="2"
                            step="0.01"
                            defaultValue="1"
                            onChange={handleGainChange}
                            className="live-slider"
                        />
                    </div>
                    <div className="live-button-container">
                        <button onClick={toggleMonitoring} className={`live-button ${isStreaming ? "blinking" : ""}`} disabled={!selectedDeviceId || isButtonDisabled}>
                            {isStreaming ? "⏹ 停止" : "🔴 3Dボイス スタート"}
                        </button>
                    </div>
                    <div className="live-button-container">
                        <button onClick={togglePassThru} className={`live-thru-button ${isPassThru ? "green-button" : ""}`} disabled={!isStreaming}>
                            {isPassThru ? "▶ 再開" : "⏸ 一時中断（スルーモード）"}
                        </button>
                    </div>
                </div>
                <div className="live-pan-container">
                    <SelectableBall
                        switchToImpulseResponse1={switchToImpulseResponse1}
                        switchToImpulseResponse2={switchToImpulseResponse2}
                        switchToImpulseResponse3={switchToImpulseResponse3}
                        switchToImpulseResponse4={switchToImpulseResponse4}
                        switchToImpulseResponse5={switchToImpulseResponse5}
                        switchToImpulseResponse6={switchToImpulseResponse6}
                        switchToImpulseResponse7={switchToImpulseResponse7}
                        switchToImpulseResponse8={switchToImpulseResponse8}
                        switchToImpulseResponse9={switchToImpulseResponse9}
                    />
                    <div className="live-tips-container">
                        <img src="live_tips.svg" alt="Live Tips" className="live-tips-image" />
                    </div>
                </div>
            </div>
        </>
    );
}

export default MicMonitor;